/**
 * AI Credits Admin Page
 *
 * Manage user AI credit balances and subscription tiers
 */
import { useMemo, useState } from "react";
import { useSnapshot } from "valtio";
import { CreditCard, Search, X, Users, TrendingDown, TrendingUp } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { EditCreditsDialog } from "@/features/admin/components/EditCreditsDialog";
import {
  aiCreditsStore,
  openEditDialog,
  setSearchQuery,
  type UserAISettings,
  type SubscriptionTier,
} from "@/features/admin/stores/ai-credits";
import { useAllUserAISettings } from "@/lib/instantdb";

// Tier badge colors
const tierStyles: Record<SubscriptionTier, string> = {
  free: "bg-slate-100 text-slate-700 border-slate-200",
  pro: "bg-blue-100 text-blue-700 border-blue-200",
  enterprise: "bg-purple-100 text-purple-700 border-purple-200",
};

const tierLabels: Record<SubscriptionTier, string> = {
  free: "FREE",
  pro: "PRO",
  enterprise: "ENT",
};

export default function AICredits() {
  const { searchQuery } = useSnapshot(aiCreditsStore);
  const { data, isLoading, error } = useAllUserAISettings();
  const [localSearch, setLocalSearch] = useState(searchQuery);

  const users = (data?.user_ai_settings ?? []) as UserAISettings[];

  // Filter users by search query
  const filteredUsers = useMemo(() => {
    if (!searchQuery) return users;
    const q = searchQuery.toLowerCase();
    return users.filter((u) => u.id.toLowerCase().includes(q));
  }, [users, searchQuery]);

  // Stats
  const stats = useMemo(() => {
    const totalUsers = users.length;
    const totalCredits = users.reduce((sum, u) => sum + (u.credits_remaining ?? 0), 0);
    const totalUsedThisMonth = users.reduce((sum, u) => sum + (u.credits_used_this_month ?? 0), 0);
    const lowBalanceUsers = users.filter((u) => {
      const remaining = u.credits_remaining ?? 0;
      const limit = u.monthly_limit ?? 0;
      return limit > 0 && remaining < limit * 0.1;
    }).length;

    return { totalUsers, totalCredits, totalUsedThisMonth, lowBalanceUsers };
  }, [users]);

  const handleSearch = () => {
    setSearchQuery(localSearch);
  };

  const handleClearSearch = () => {
    setLocalSearch("");
    setSearchQuery("");
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const formatCurrency = (value: number) => {
    return `$${value.toFixed(2)}`;
  };

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <header className="shrink-0 border-b bg-background px-4 py-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="flex items-center gap-3">
            <CreditCard className="h-8 w-8 text-emerald-600" />
            <div>
              <h1 className="text-3xl font-bold">AI Credits</h1>
              <p className="mt-1 text-muted-foreground">
                Manage user credit balances and subscription tiers
              </p>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
          {/* Stats cards */}
          <div className="mb-6 grid grid-cols-2 gap-4 sm:grid-cols-4">
            <StatsCard
              icon={<Users className="h-5 w-5 text-slate-500" />}
              label="Total Users"
              value={stats.totalUsers}
              loading={isLoading}
            />
            <StatsCard
              icon={<TrendingUp className="h-5 w-5 text-emerald-500" />}
              label="Total Credits"
              value={formatCurrency(stats.totalCredits)}
              loading={isLoading}
            />
            <StatsCard
              icon={<TrendingDown className="h-5 w-5 text-blue-500" />}
              label="Used This Month"
              value={formatCurrency(stats.totalUsedThisMonth)}
              loading={isLoading}
            />
            <StatsCard
              icon={<CreditCard className="h-5 w-5 text-amber-500" />}
              label="Low Balance"
              value={stats.lowBalanceUsers}
              loading={isLoading}
            />
          </div>

          {/* Search */}
          <Card className="mb-6">
            <CardContent className="pt-6">
              <div className="flex gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    placeholder="Search by user ID..."
                    value={localSearch}
                    onChange={(e) => setLocalSearch(e.target.value)}
                    onKeyDown={handleKeyDown}
                    className="pl-10 pr-10"
                  />
                  {localSearch && (
                    <button
                      type="button"
                      onClick={handleClearSearch}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
                <Button onClick={handleSearch}>Search</Button>
              </div>
            </CardContent>
          </Card>

          {/* Error state */}
          {error && (
            <div className="mb-6 rounded-lg border border-red-200 bg-red-50 p-4 text-red-700">
              Failed to load users: {error.message}
            </div>
          )}

          {/* Users table */}
          <Card>
            <CardHeader>
              <CardTitle>
                Users ({filteredUsers.length}
                {searchQuery && ` of ${users.length}`})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="space-y-2">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <Skeleton key={i} className="h-12" />
                  ))}
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>User ID</TableHead>
                        <TableHead>Tier</TableHead>
                        <TableHead className="text-right">Remaining</TableHead>
                        <TableHead className="text-right">Limit</TableHead>
                        <TableHead className="text-right">Used</TableHead>
                        <TableHead>Last Reset</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredUsers.length > 0 ? (
                        filteredUsers.map((user) => (
                          <UserRow
                            key={user.id}
                            user={user}
                            onEdit={() => openEditDialog(user)}
                          />
                        ))
                      ) : (
                        <TableRow>
                          <TableCell
                            colSpan={7}
                            className="py-8 text-center text-muted-foreground"
                          >
                            {searchQuery
                              ? "No users found matching your search"
                              : "No users found"}
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Edit dialog */}
      <EditCreditsDialog />
    </div>
  );
}

// ========== Sub-components ==========

interface StatsCardProps {
  icon: React.ReactNode;
  label: string;
  value: number | string;
  loading?: boolean;
}

function StatsCard({ icon, label, value, loading }: StatsCardProps) {
  return (
    <Card className="flex items-center gap-3 p-4">
      {icon}
      <div>
        {loading ? (
          <Skeleton className="mb-1 h-7 w-16" />
        ) : (
          <p className="text-2xl font-bold">{value}</p>
        )}
        <p className="text-sm text-muted-foreground">{label}</p>
      </div>
    </Card>
  );
}

interface UserRowProps {
  user: UserAISettings;
  onEdit: () => void;
}

function UserRow({ user, onEdit }: UserRowProps) {
  const tier = (user.subscription_tier as SubscriptionTier) || "free";
  const creditsRemaining = user.credits_remaining ?? 0;
  const monthlyLimit = user.monthly_limit ?? 0;
  const usedThisMonth = user.credits_used_this_month ?? 0;

  const isLowBalance = monthlyLimit > 0 && creditsRemaining < monthlyLimit * 0.1;
  const isOverLimit = monthlyLimit > 0 && usedThisMonth >= monthlyLimit;

  const formatDate = (isoString: string | undefined) => {
    if (!isoString) return "-";
    try {
      return new Date(isoString).toLocaleDateString();
    } catch {
      return "-";
    }
  };

  return (
    <TableRow>
      <TableCell className="max-w-[200px] truncate font-mono text-sm">
        {user.id}
      </TableCell>
      <TableCell>
        <Badge
          variant="outline"
          className={tierStyles[tier] || tierStyles.free}
        >
          {tierLabels[tier] || "FREE"}
        </Badge>
      </TableCell>
      <TableCell
        className={`text-right font-medium ${isLowBalance ? "text-amber-600" : ""}`}
      >
        ${creditsRemaining.toFixed(2)}
      </TableCell>
      <TableCell className="text-right">
        ${monthlyLimit.toFixed(2)}
      </TableCell>
      <TableCell
        className={`text-right ${isOverLimit ? "text-red-600 font-medium" : ""}`}
      >
        ${usedThisMonth.toFixed(2)}
      </TableCell>
      <TableCell className="text-muted-foreground">
        {formatDate(user.last_credit_reset)}
      </TableCell>
      <TableCell className="text-right">
        <Button variant="ghost" size="sm" onClick={onEdit}>
          Edit
        </Button>
      </TableCell>
    </TableRow>
  );
}
